import { useAuth } from 'context/auth';
import { PatchScoringSettings } from 'crono-fe-common/types/DTO/patchScoringSettings';
import { Prospect } from 'crono-fe-common/types/prospect';
import { ProspectLinkedin } from 'crono-fe-common/types/prospectLinkedin';
import { ProspectSearchStrategyResponse } from 'crono-fe-common/types/prospectSearchStrategy';
import { checkIfLeadsEnabled } from 'crono-fe-common/utils';
import { useGetScoreSettings } from 'hooks/services/score-settings/useGetScoreSettings';
import { checkIsProspectLead } from 'utils/businessLogic';

export const useProspectScoreSettings = (
  type: PatchScoringSettings['type'],
) => {
  const { user } = useAuth();
  const { hasLeadScoreAccess: hasLeadScoreAccessProspect } = useLeadScoreAccess(
    ['Prospect'],
  );
  const { hasLeadScoreAccess: hasLeadScoreAccessLead } = useLeadScoreAccess([
    'Lead',
  ]);

  const { data: leadScoreSettingsData } = useGetScoreSettings(
    'Lead',
    type === 'Lead' && hasLeadScoreAccessLead,
  );
  const { data: prospectScoreSettingsData } = useGetScoreSettings(
    'Prospect',
    type === 'Prospect' && hasLeadScoreAccessProspect,
  );

  const leadScoreSettings = leadScoreSettingsData?.data?.data;
  const prospectScoreSettings = prospectScoreSettingsData?.data?.data;

  const getProspectOrLeadSettings = (
    prospect?:
      | Prospect
      | ProspectSearchStrategyResponse
      | ProspectLinkedin
      | null,
  ) => {
    if (!prospect) {
      return null;
    }

    return checkIsProspectLead(user, prospect)
      ? leadScoreSettings
      : prospectScoreSettings;
  };

  return {
    getProspectOrLeadSettings,
    leadScoreSettings,
    prospectScoreSettings,
  };
};

export const useAccountScoreSettings = () => {
  const { hasLeadScoreAccess } = useLeadScoreAccess(['Account']);
  const { data: accountScoreSettingsData } = useGetScoreSettings(
    'Account',
    hasLeadScoreAccess,
  );
  const accountScoreSettings = accountScoreSettingsData?.data?.data;

  return {
    accountScoreSettings,
  };
};

export const useLeadScoreAccess = (types?: PatchScoringSettings['type'][]) => {
  const { user } = useAuth();

  const keyByType = {
    Account: 'accountScoreSettingsEnabled',
    Prospect: 'prospectScoreSettingsEnabled',
    Lead: 'leadScoreSettingsEnabled',
  } as const;

  const hasLeadScoreAccess = !!(
    (!types?.length ||
      types.some((type) => user?.otherSettings?.[keyByType[type]])) &&
    user?.otherSettings?.hasLeadScore &&
    (types?.every((type) => type === 'Lead') ? checkIfLeadsEnabled(user) : true)
  );

  const shouldShowScoreForProspect = (
    prospect:
      | Prospect
      | ProspectSearchStrategyResponse
      | ProspectLinkedin
      | null
      | undefined,
  ) => {
    if (!user || !prospect) {
      return false;
    }

    if (checkIsProspectLead(user, prospect)) {
      return !!(
        user.otherSettings?.leadScoreSettingsEnabled &&
        user?.otherSettings?.hasLeadScore
      );
    }

    return !!(
      user.otherSettings?.prospectScoreSettingsEnabled &&
      user?.otherSettings?.hasLeadScore
    );
  };

  function checkHasLeadScoreAccess(types?: PatchScoringSettings['type'][]) {
    return !!(
      user?.otherSettings?.hasLeadScore &&
      (types?.every((type) => type === 'Lead')
        ? checkIfLeadsEnabled(user)
        : true)
    );
  }

  return {
    hasLeadScoreAccess,
    shouldShowScoreForProspect,
    checkHasLeadScoreAccess,
  };
};
